/**
 * Metrics Strip Component
 * Inline horizontal row of metrics separated by vertical dividers.
 * Designed to be embedded inside a parent Card (no Card wrapper of its own).
 */
import React from 'react';
import { Box, Divider, Typography, Skeleton } from '@mui/material';

interface MetricItem {
  label: string;
  value: number | string;
  caption?: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

interface MetricsStripProps {
  items: MetricItem[];
  loading?: boolean;
}

export default function MetricsStrip({ items, loading }: MetricsStripProps) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 3 }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rectangular" width={100} height={48} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap' }}>
      {items.map((item, index) => (
        <React.Fragment key={item.label}>
          {index > 0 && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            />
          )}
          <Box
            sx={{
              flex: 1,
              minWidth: { xs: '45%', sm: 100 },
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              ...(item.onClick && {
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                },
                '&:hover .metric-label': {
                  textDecoration: 'underline',
                  color: '#6366F1',
                },
              }),
            }}
            onClick={item.onClick}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                '& svg': { fontSize: 20 },
              }}
            >
              {item.icon}
            </Box>
            <Box>
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.2, color: 'text.primary' }}
              >
                {item.value}
              </Typography>
              <Typography
                className="metric-label"
                variant="body2"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  transition: 'color 0.15s',
                }}
              >
                {item.label}
              </Typography>
              {item.caption && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.disabled', fontSize: '0.75rem', lineHeight: 1.2 }}
                >
                  {item.caption}
                </Typography>
              )}
            </Box>
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
}
